import * as React from 'react';
import OutputOptionsRadioRow from './OutputOptionsRadioRow';
import OutputOptionsCheckboxRow from './OutputOptionsCheckboxRow';
import apiClient, { WordSense } from './apiClient';

type outputContentChoice = 'words' | 'sentences';
type outputListStyleChoice = 'none' | '1' | 'hash' | 'star';
type outputFormatChoice = 'oneline' | 'multiline' | 'wiki';
type outputIncludeChoice = 'hanzi' | 'pinyin' | 'english';

interface State {
  outputContent: outputContentChoice;
  outputListStyle: outputListStyleChoice;
  outputFormat: outputFormatChoice;
  outputInclude: Set<outputIncludeChoice>;
  sentencePinyin: { [sentence: string]: string };
  wordSense: { [word: string]: WordSense };
}

interface Props {
  processedInput: string;
}

export default class OutputsBox extends React.Component<Props> {

  state: State = {
    outputContent: 'words',
    outputListStyle: 'none',
    outputFormat: 'oneline',
    outputInclude: new Set(['hanzi', 'pinyin']),
    sentencePinyin: {},
    wordSense: {},
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.processedInput !== this.props.processedInput) {
      this.loadCaineData();
    }
  }

  loadCaineData() {
    if (this.getOutputContent() === 'sentences') {
      this.getLines().forEach(async (sentence) => {
        if (!this.state.sentencePinyin[sentence]) {
          // strip leading *'s from the sentence
          const pinyin = await apiClient.sentenceToPinyin(sentence.replace(/^\*\s*/, ''));
          this.setState({
            sentencePinyin: {
              ...this.state.sentencePinyin,
              [sentence]: pinyin,
            },
          });
        }
      });
    } else {
      this.getLines().forEach(async (word) => {
        if (!(word in this.state.wordSense)) {
          const sense = await apiClient.wordToSense(word);
          this.setState({
            wordSense: {
              ...this.state.wordSense,
              [word]: sense,
            },
          });
        }
      });
    }
  }

  getLines() {
    return this.props.processedInput.split(/\n/ig).filter(line => !line.match(/^\s*$/));
  }

  hasNeededParts() {
    if (this.getOutputContent() === 'sentences') {
      if (!this.state.outputInclude.has('pinyin')) return true;
      const sentences = this.getLines();
      for (const sentence of sentences) {
        if (!this.state.sentencePinyin[sentence]) return false;
      }
      return true;
    }

    if (!this.state.outputInclude.has('pinyin') && !this.state.outputInclude.has('english')) return true;
    const words = this.getLines();
    for (const word of words) {
      if (!(word in this.state.wordSense)) return false;
    }
    return true;
  }

  getOutputContent(): outputContentChoice {
    return this.allowsWordsSelection() ? this.state.outputContent : 'sentences';
  }

  getOutputInclude(): Set<outputIncludeChoice> {
    const modifiedInclude = new Set(this.state.outputInclude);
    if (modifiedInclude.has('english') && !this.allowsEnglishSelection()) {
      modifiedInclude.delete('english');
    }
    return modifiedInclude;
  }

  allowsWordsSelection() {
    return !this.props.processedInput.match(/[\.\?!。？！,，]/uig);
  }

  allowsEnglishSelection() {
    return this.allowsWordsSelection() && this.state.outputContent === 'words';
  }

  getListPrefix(): string {
    if (this.state.outputListStyle === 'none') return '';
    if (this.state.outputListStyle === '1') return '1. ';
    if (this.state.outputListStyle === 'hash') return '# ';
    if (this.state.outputListStyle === 'star') return '* ';
  }

  refresh() {
    apiClient.reset();
    this.setState({ sentencePinyin: {}, wordSense: {} }, () => this.loadCaineData());
  }

  formatLine(line: string) {
    if (line.match(/^\s*$/)) return line;
    let formattedLine = this.getListPrefix();
    const lineParts: string[] = [];
    if (this.state.outputInclude.has('hanzi')) {
      lineParts.push(line);
    }
    if (this.state.outputInclude.has('pinyin')) {
      let pinyin;
      if (this.getOutputContent() === 'sentences') {
        pinyin = this.state.sentencePinyin[line];
      } else if (this.state.wordSense[line]) {
        pinyin = this.state.wordSense[line].pinyin;
      }
      if (pinyin) {
        if (this.state.outputFormat === 'oneline') {
          lineParts.push(`(${pinyin})`);
        } else if (this.state.outputFormat === 'multiline') {
          lineParts.push(pinyin);
        } else {
          lineParts.push(`<span class="pinyin">${pinyin}</span>`);
        }
      }
    }
    if (
      this.getOutputContent() === 'words' &&
      this.getOutputInclude().has('english')
    ) {
      if (this.state.wordSense[line]) {
        const english = this.state.wordSense[line].sense;
        if (this.state.outputFormat === 'wiki') {
          lineParts.push(`<span class="trans">${english}</span>`);
        } else {
          lineParts.push(english);
        }
      } else if (this.state.outputFormat === 'wiki') {
        lineParts.push('<span class="trans"></span>');
      }
    }
    if (this.state.outputFormat === 'multiline') {
      formattedLine += `${lineParts.join('\n')}\n`;
    } else {
      formattedLine += lineParts.join(' ');
    }
    return formattedLine;
  }

  getOutput() {
    if (!this.hasNeededParts()) return '';
    const formattedLines = this.props.processedInput.split(/\n/ig).map(line => this.formatLine(line));
    return formattedLines.join('\n');
  }

  isLoading() {
    return !this.hasNeededParts();
  }

  render() {
    return (
      <div>
        <div className="pull-right">
          <button
            className="btn btn-default btn-sm"
            onClick={() => this.refresh()}
            disabled={this.getLines().length === 0}
          >
            Refresh
          </button>
        </div>
        <h4>Output / 输出</h4>
        <form className="form-horizontal">
          <OutputOptionsRadioRow
            label="Content"
            name="content"
            choices={{ words: 'Words', sentences: 'Sentences' }}
            selected={this.getOutputContent()}
            isChoiceDisabled={choice => choice === 'words' && !this.allowsWordsSelection()}
            onChange={outputContent => this.setState({ outputContent }, () => this.loadCaineData())}
          />
          <OutputOptionsRadioRow
            label="List"
            name="list"
            choices={{ none: 'No', 1: '1.', hash: '#', star: '*' }}
            selected={this.state.outputListStyle}
            onChange={outputListStyle => this.setState({ outputListStyle })}
          />
          <OutputOptionsRadioRow
            label="Format"
            name="format"
            choices={{ oneline: '1-line', multiline: 'multi-line', wiki: 'Wiki' }}
            selected={this.state.outputFormat}
            onChange={outputFormat => this.setState({ outputFormat })}
          />
          <OutputOptionsCheckboxRow
            label="Include"
            choices={{ hanzi: 'Hanzi', pinyin: 'Pinyin', english: 'English' }}
            selected={this.state.outputInclude}
            isChoiceDisabled={choice => choice === 'english' && !this.allowsEnglishSelection()}
            onChange={outputInclude => this.setState({ outputInclude })}
          />
        </form>
        <textarea
          className="form-control"
          disabled={this.isLoading()}
          value={this.isLoading() ? 'loading...' : this.getOutput()}
        />
      </div>
    );
  }
}
